import { useAppForm } from '@sparklayer-io/spark-dash-core/lib/app-form';
import { ApiResponseError } from '@sparklayer-io/spark-dash-core/types/errors';
import z from '@sparklayer-io/spark-dash-core/zod';
import { Input, LearnMoreLink, Modal } from '@ui/themed';
import { t } from 'i18next';
import { useState } from 'react';
import type { I18nPrefix } from '../../../i18n/prefix';
import type { DashboardHandlers } from '../../../lib/dashboard-page';
import { AuthAlert } from '../../auth/components/auth-alert';

const COMPONENT = 'features:no_team.components.create_team_form' satisfies I18nPrefix;

const APP_REGISTRATION_SOURCE = 'dashboard_new_team';

const createTeamSchema = z.object({
  companyName: z.string().min(1),
  companyWebsite: z
    .string()
    .min(1)
    .refine((domain) => z.url().safeParse(`https://${domain}`).success, {
      message: t(`${COMPONENT}.company_website_invalid`),
    }),
});

type CreateTeamResponse = { team: { id: string } };
type CreateTeamValidationError = { errors: Record<string, string[]> };

type CreateTeamFormProps = {
  handlers: DashboardHandlers;
  isOpen: boolean;
  onClose: () => void;
  onTeamCreated: (teamId: string) => void;
};

export function CreateTeamForm({ handlers, isOpen, onClose, onTeamCreated }: CreateTeamFormProps) {
  const { apiHandler } = handlers;
  const [errors, setErrors] = useState<Record<string, string[]>>({});
  const [genericError, setGenericError] = useState<string | null>(null);

  const form = useAppForm({
    defaultValues: { companyName: '', companyWebsite: '' },
    validators: { onSubmit: createTeamSchema },
    onSubmit: async ({ value }) => {
      setErrors({});
      setGenericError(null);
      try {
        const data = await apiHandler.fetch<CreateTeamResponse>({
          method: 'POST',
          path: '/team',
          payload: {
            company_name: value.companyName,
            company_website: value.companyWebsite ? `https://${value.companyWebsite}` : '',
            app_registration_source: APP_REGISTRATION_SOURCE,
            is_partner: false,
          },
        });
        form.reset();
        onTeamCreated(data.team.id);
      } catch (err) {
        if (err instanceof ApiResponseError && err.status === 422) {
          setErrors((err.data as CreateTeamValidationError).errors);
        } else {
          setGenericError(t('errors:generic'));
        }
      }
    },
  });

  function handleClose() {
    form.reset();
    setErrors({});
    setGenericError(null);
    onClose();
  }

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      primaryAction={{
        text: t(`${COMPONENT}.submit`),
        onClick: form.handleSubmit,
        loading: form.state.isSubmitting,
      }}
      secondaryAction={{ text: t(`${COMPONENT}.cancel`), onClick: handleClose }}
    >
      <Modal.Header>{t(`${COMPONENT}.heading`)}</Modal.Header>
      <Modal.Body>
        {genericError && <AuthAlert type="error">{genericError}</AuthAlert>}
        <p className="text-gray-600 text-sm">
          {t(`${COMPONENT}.description`)}{' '}
          <LearnMoreLink link="https://docs.sparklayer.io/account-settings#nQ3DC" />
        </p>

        <form
          className="mt-4 flex flex-col gap-4"
          onSubmit={(event) => {
            event.preventDefault();
            event.stopPropagation();
            void form.handleSubmit();
          }}
        >
          <form.Field name="companyName">
            {(field) => (
              <Input
                label={t(`${COMPONENT}.company_name_label`)}
                description={t(`${COMPONENT}.company_name_description`)}
                value={field.state.value}
                onChange={field.handleChange}
                error={field.state.meta.errors[0]?.message ?? errors.company_name?.[0]}
              />
            )}
          </form.Field>

          <form.Field name="companyWebsite">
            {(field) => (
              <Input
                label={t(`${COMPONENT}.company_website_label`)}
                description={t(`${COMPONENT}.company_website_description`)}
                prefix="https://"
                value={field.state.value}
                onChange={(value) => field.handleChange(value.trim().replace(/^https?:\/\//, ''))}
                error={field.state.meta.errors[0]?.message ?? errors.company_website?.[0]}
              />
            )}
          </form.Field>
        </form>
      </Modal.Body>
    </Modal>
  );
}
